import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { API_BASE_URL } from '../utils/config';
import { notifySuccess, notifyError } from '../helper/notify';

const CLEANUP_API = `${API_BASE_URL}/api/sellvia-cleanup`;

// Same pattern as BulkJobContext — lives above <Routes> in App.jsx so it
// survives navigating between admin pages, and the backend job itself keeps
// running (Manual batches, Auto's own scheduler) whether or not anyone is
// watching; this just re-attaches the UI to whatever's currently running.
const SellviaCleanupJobContext = createContext(null);

export const SellviaCleanupJobProvider = ({ children }) => {
    const { token: authToken } = useSelector((s) => s.auth);
    const [job, setJob] = useState(null); // { _id, type, status, total, current, success, failed, status_message, store_name }
    const pollRef = useRef(null);
    // Job ids we've already shown the "cleanup done" toast for — guards
    // against showing it more than once for the SAME job even if this
    // effect/poll cycle somehow re-fires for it (e.g. the 8s "watch for a
    // new job" check re-discovering it in the brief window right around
    // completion). A small capped list, not meant to grow forever.
    const notifiedJobIdsRef = useRef([]);

    const stopPolling = useCallback(() => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }, []);

    const poll = useCallback((jobId, token) => {
        stopPolling();
        pollRef.current = setInterval(async () => {
            try {
                const res = await axios.get(`${CLEANUP_API}/jobs/${jobId}`, { headers: { Authorization: `Bearer ${token}` } });
                const j = res.data.job;
                if (!j) { stopPolling(); setJob(null); return; }
                setJob(j);

                if (j.status !== 'running') {
                    stopPolling();
                    // Only ever toast once per job id, no matter how many times
                    // this branch gets reached for it.
                    if (!notifiedJobIdsRef.current.includes(j._id)) {
                        notifiedJobIdsRef.current = [...notifiedJobIdsRef.current.slice(-19), j._id];
                        if (j.status === 'cancelled') {
                            notifyError(`Cancelled — ${j.success} done, ${j.failed} failed`);
                        } else if (j.failed > 0 && /plugin not connected/i.test(j.status_message || '')) {
                            // At least one order failed specifically because its store's
                            // WordPress plugin isn't connected — that's not a real
                            // "failure", just something the user needs to go do first, and
                            // it auto-retries on its own once the plugin connects. Surface
                            // that exact, already-clear reason (the same line the pipeline
                            // logs and the live banner shows) instead of the vague
                            // "X processed, Y failed" count.
                            notifyError(j.status_message);
                        } else {
                            const msg = j.failed === 0
                                ? `MarketSync cleanup done — ${j.success} order${j.success !== 1 ? 's' : ''} processed`
                                : `MarketSync cleanup done — ${j.success} processed, ${j.failed} failed`;
                            j.failed > 0 ? notifyError(msg) : notifySuccess(msg);
                        }
                    }
                    // Auto-close the banner a couple seconds after it lands on a
                    // final state, matching the "close automatically" behaviour.
                    setTimeout(() => setJob((cur) => (cur && cur._id === j._id ? null : cur)), 2500);
                }
            } catch (e) { console.error('[sellvia-cleanup] poll error:', e.message); }
        }, 1200);
    }, [stopPolling]);

    const startJob = useCallback((j) => {
        setJob(j);
        if (authToken) poll(j._id, authToken);
    }, [poll, authToken]);

    const cancelJob = useCallback(async () => {
        if (!job?._id || !authToken) return;
        try {
            await axios.post(`${CLEANUP_API}/jobs/${job._id}/cancel`, {}, { headers: { Authorization: `Bearer ${authToken}` } });
        } catch (e) { console.error('[sellvia-cleanup] cancel failed:', e.message); }
    }, [job, authToken]);

    // Resume an in-progress job (manual batch, or an auto-run mid-flight) on
    // login/refresh/navigating between pages.
    useEffect(() => {
        if (!authToken) { stopPolling(); setJob(null); return; }
        axios.get(`${CLEANUP_API}/jobs/active`, { headers: { Authorization: `Bearer ${authToken}` } })
            .then((res) => { if (res.data.job) startJob(res.data.job); })
            .catch(() => {});
        // Poll for a NEW job appearing (e.g. the Auto scheduler starting one in
        // the background) every few seconds while nothing is currently shown.
        const watchInterval = setInterval(() => {
            setJob((cur) => {
                if (cur) return cur; // already tracking one — the poll() above handles it
                axios.get(`${CLEANUP_API}/jobs/active`, { headers: { Authorization: `Bearer ${authToken}` } })
                    .then((res) => { if (res.data.job) startJob(res.data.job); })
                    .catch(() => {});
                return cur;
            });
        }, 8000);
        return () => { stopPolling(); clearInterval(watchInterval); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken]);

    return (
        <SellviaCleanupJobContext.Provider value={{ job, startJob, cancelJob }}>
            {children}
        </SellviaCleanupJobContext.Provider>
    );
};

export const useSellviaCleanupJob = () => useContext(SellviaCleanupJobContext);
